import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import UserDropdown from '../UserDropdown';
import CancelRideModal from '../Shared/CancelRideModal';
import ContactModal from '../common/ContactModal';
import ConfirmModal from '../common/ConfirmModal';
import { formatDateTime, ensureUTC } from '../../utils/dateUtils';
import AppDownloadModal from '../common/AppDownloadModal';
import { isNativeApp } from '../../utils/platform';

// Stripe fee calculation helpers (pass-through formula)
const calculateProcessingFee = (fare) => {
  const amount = parseFloat(fare);
  if (amount <= 0) return 0;
  return Math.ceil(((amount + 0.30) / 0.971 - amount) * 100) / 100;
};
const calculateTotalWithFee = (fare) => {
  const amount = parseFloat(fare);
  if (amount <= 0) return 0;
  return Math.ceil((amount + 0.30) / 0.971 * 100) / 100;
};

// Active Ride Card Component for Riders
const ActiveRideCard = ({ ride, onComplete, onCancel }) => {
  const navigate = useNavigate();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  const getRideStatus = () => {
    const now = new Date();
    const startTime = new Date(ensureUTC(ride.pickup_datetime));
    const fifteenMinBefore = new Date(startTime.getTime() - 15 * 60 * 1000);

    if (now < fifteenMinBefore) {
      return { label: 'Upcoming', color: 'bg-gray-100 border-gray-300', textColor: 'text-gray-700' };
    } else if (now < startTime) {
      return { label: 'Starting Soon', color: 'bg-yellow-50 border-yellow-400', textColor: 'text-yellow-800' };
    } else {
      // Overdue = time since pickup was supposed to happen (based on pickup time only)
      const minutesOverdue = Math.floor((now - startTime) / 1000 / 60);
      return {
        label: `${minutesOverdue} min overdue`,
        color: 'bg-red-50 border-red-400',
        textColor: 'text-red-800'
      };
    }
  };

  const formatTime = (datetime) => {
    return new Date(datetime).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      
    });
  };

  const formatDate = (datetime) => {
    return new Date(datetime).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      
    });
  };

  const [completing, setCompleting] = useState(false);

  const handleCompleteClick = async () => {
    // For day booking legs
    if (ride.is_day_booking_leg) {
      try {
        setCompleting(true);
        const token = localStorage.getItem('token');

        // Complete the leg via API first
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/day-bookings/legs/${ride.leg_id}/complete`,
          { completed_by: 'rider' },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (ride.is_final_leg) {
          // Final leg - navigate to tip page after completing
          navigate(`/day-bookings/${ride.booking_id}/tip`);
        } else {
          // Interim leg - just show success and refresh
          alert('Leg completed!');
          onComplete && onComplete();
        }
      } catch (error) {
        console.error('Error completing leg:', error);
        const errorDetail = error.response?.data?.detail;
        const errorMessage = typeof errorDetail === 'string'
          ? errorDetail
          : (errorDetail?.message || JSON.stringify(errorDetail) || 'Failed to complete leg. Please try again.');
        alert(errorMessage);
      } finally {
        setCompleting(false);
      }
    } else {
      // Regular ride - navigate to completion page with tip interface
      navigate(`/rides/${ride.id}/complete`);
    }
  };

  const status = getRideStatus();

  // Check if ride can be marked complete (pickup time has passed)
  const now = new Date();
  const pickupTime = new Date(ensureUTC(ride.pickup_datetime));
  const canMarkComplete = now >= pickupTime;

  return (
    <div className={`rounded-lg border-2 ${status.color} p-4 mb-3 shadow-sm transition-all hover:shadow-md`}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-semibold px-2 py-1 rounded ${status.color} ${status.textColor}`}>
              {status.label}
            </span>
            <span className="text-sm font-medium text-gray-600">
              {formatDate(ride.pickup_datetime)}
            </span>
          </div>
          <h3 className="text-lg font-bold text-gray-900">
            {ride.driver_name || 'Driver'}
          </h3>
        </div>
        <div className="text-right">
          {ride.is_day_booking_leg ? (
            <>
              <div className="text-lg font-bold text-orange-600">
                Leg {ride.sequence_number || 1} of {ride.max_sequence || '?'}
              </div>
              <div className="text-xs text-orange-500">
                {ride.rate_type === 'half_day' ? 'Half-Day' : 'Full-Day'} Booking
              </div>
            </>
          ) : (
            <div className="text-2xl font-bold text-green-600">
              ${(ride.rider_total || ride.rider_fare_total || ((ride.total_fare || 0) + (ride.processing_fee || ride.fare_processing_fee || 0)) || ride.final_price || 0).toFixed(2)}
            </div>
          )}
        </div>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-start gap-2">
          <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" strokeWidth="2"/>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3"/>
          </svg>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">
              {formatTime(ride.pickup_datetime)}
              {ride.dropoff_datetime && ` - ${formatTime(ride.dropoff_datetime)}`}
            </p>
            <p className="text-xs text-gray-500">
              {ride.duration_minutes ? `${ride.duration_minutes} minutes` : ''}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-2">
          <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
          </svg>
          <div className="flex-1">
            <p className="text-sm text-gray-700">
              <span className="font-medium">From:</span> {ride.pickup_address || ride.pickup_location}
            </p>
            <p className="text-sm text-gray-700">
              <span className="font-medium">To:</span> {ride.dropoff_address || ride.dropoff_location}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {ride.distance_miles} miles
            </p>
          </div>
        </div>
      </div>

      {/* Contact Driver Button */}
      <div className="mb-4">
        <button
          onClick={() => setShowContactModal(true)}
          className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          Contact Driver
        </button>
      </div>

      <div className="flex gap-2">
        {canMarkComplete ? (
          <button
            onClick={handleCompleteClick}
            disabled={completing}
            className={`flex-1 px-4 py-3 rounded-lg font-semibold text-white transition-colors ${
              completing ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {completing ? 'Completing...' : (
              ride.is_day_booking_leg && !ride.is_final_leg ? 'Mark Complete' : 'Complete & Tip'
            )}
          </button>
        ) : (
          <div className="flex-1 px-4 py-3 rounded-lg font-semibold text-center bg-gray-200 text-gray-500">
            Not Started Yet
          </div>
        )}

        <button
          onClick={() => setShowCancelModal(true)}
          className="px-4 py-3 rounded-lg font-semibold transition-colors border-2 bg-white text-orange-600 border-orange-300 hover:bg-orange-50"
          title="Cancel ride"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <CancelRideModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        ride={ride}
        userType="rider"
        onCancellationSuccess={() => {
          setShowCancelModal(false);
          onCancel && onCancel();
        }}
      />

      {/* Contact Driver Modal */}
      <ContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        contactName={ride.driver_name}
        contactPhone={ride.driver_phone}
        contactType="driver"
      />
    </div>
  );
};

// Upcoming Ride Card Component (for scheduled rides not yet active)
const UpcomingRideCard = ({ ride, onCancel }) => {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  return (
    <div className="border rounded-lg p-4 hover:bg-gray-50 relative">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          {ride.driver_name && (
            <div className="text-sm font-medium text-blue-600 mb-2">
              🚗 Driver: {ride.driver_name}
            </div>
          )}
          <div className="font-semibold">
            {ride.pickup_address || ride.pickup_location} → {ride.dropoff_address || ride.dropoff_location}
          </div>
          <div className="text-sm text-gray-600 mt-1">
            {formatDateTime(ride.pickup_datetime, 'full')}
          </div>
          <div className="text-sm text-gray-600">
            ${(ride.rider_total || ride.rider_fare_total || ((ride.total_fare || 0) + (ride.processing_fee || ride.fare_processing_fee || 0)) || ride.final_price || 0).toFixed(2)}
          </div>
        </div>
        <button
          onClick={() => setShowCancelModal(true)}
          className="ml-2 p-2 rounded-full hover:bg-red-50 text-red-600 transition-colors"
          title="Cancel ride"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Contact Driver Button */}
      <div className="mt-3">
        <button
          onClick={() => setShowContactModal(true)}
          className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors text-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          Contact Driver
        </button>
      </div>

      <CancelRideModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        ride={ride}
        userType="rider"
        onCancellationSuccess={() => {
          setShowCancelModal(false);
          onCancel && onCancel();
        }}
      />

      {/* Contact Driver Modal */}
      <ContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        contactName={ride.driver_name}
        contactPhone={ride.driver_phone}
        contactType="driver"
      />
    </div>
  );
};

// Day Booking Cancel Modal Component
const DayBookingCancelModal = ({ booking, type, onClose, onWithdraw, onCancel, calculateRefundEstimate }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const refundEstimate = type === 'cancel' ? calculateRefundEstimate(booking) : null;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      if (type === 'withdraw') {
        await onWithdraw(booking.id);
      } else {
        await onCancel(booking.id, reason);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (datetime) => {
    const timeStr = datetime.endsWith('Z') ? datetime : datetime + 'Z';
    return new Date(timeStr).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-12 h-12 ${type === 'withdraw' ? 'bg-yellow-100' : 'bg-red-100'} rounded-full flex items-center justify-center`}>
              <svg className={`w-6 h-6 ${type === 'withdraw' ? 'text-yellow-600' : 'text-red-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {type === 'withdraw' ? 'Withdraw Day Booking?' : 'Cancel Day Booking?'}
              </h2>
              <p className="text-sm text-gray-600">
                {booking.rate_type === 'half_day' ? 'Half-Day' : 'Full-Day'} Booking
              </p>
            </div>
          </div>

          {/* Booking Details */}
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              <strong>Date:</strong> {new Date(booking.start_time.endsWith('Z') ? booking.start_time : booking.start_time + 'Z').toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                
              })}
            </p>
            <p className="text-sm text-gray-600 mb-2">
              <strong>Time:</strong> {formatTime(booking.start_time)} - {formatTime(booking.end_time)} CT
            </p>
            <p className="text-sm text-gray-600 mb-2">
              <strong>Driver:</strong> {booking.driver_name || 'N/A'}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Total:</strong> ${booking.rider_total ? booking.rider_total.toFixed(2) : booking.total_fare}
            </p>
          </div>

          {/* Refund Info */}
          {type === 'withdraw' ? (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>No Charge:</strong> Since the driver has not accepted yet, no payment has been processed. You can withdraw without any charges.
              </p>
            </div>
          ) : (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-semibold text-blue-900 mb-3">Refund Information</h3>

              <div className="mb-3 p-3 bg-white border border-blue-300 rounded">
                <p className="text-base font-bold text-blue-900">
                  You are cancelling {refundEstimate?.scenario}, and you are entitled to a {refundEstimate?.percentage}% refund
                </p>
                <p className="text-sm mt-2 text-blue-800">
                  <strong>Estimated refund:</strong> ${Math.round(refundEstimate?.amount || 0)}
                </p>
              </div>

              <div className="text-sm text-gray-600 space-y-2">
                <p className="font-medium mb-2 text-gray-700">Cancellation Policy:</p>
                <ul className="list-disc list-inside space-y-1.5 pl-2">
                  <li>Within 5 minutes of booking: No charge</li>
                  <li>24+ hours before booking: No charge</li>
                  <li>12-24 hours before booking: 50% of fare charged</li>
                  <li>6-12 hours before booking: 75% of fare charged</li>
                  <li>Less than 6 hours before booking: Full fare charged</li>
                </ul>
              </div>
            </div>
          )}

          {/* Reason Input (for cancellations only) */}
          {type === 'cancel' && (
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Reason for cancellation (optional)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                rows="3"
                placeholder="Let the driver know why you're cancelling..."
                disabled={loading}
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
            >
              Keep Booking
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading}
              className={`flex-1 px-4 py-2 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed ${
                type === 'withdraw'
                  ? 'bg-yellow-600 hover:bg-yellow-700'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {loading
                ? (type === 'withdraw' ? 'Withdrawing...' : 'Cancelling...')
                : (type === 'withdraw' ? 'Yes, Withdraw' : 'Confirm Cancellation')
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const RiderDashboard = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [upcomingRides, setUpcomingRides] = useState([]);
  const [dayBookings, setDayBookings] = useState([]);
  const [completedRides, setCompletedRides] = useState([]);
  const [rideRequests, setRideRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [withdrawalModal, setWithdrawalModal] = useState({ isOpen: false, request: null });
  const [favoriteDrivers, setFavoriteDrivers] = useState([]);
  const [showBookNowDropdown, setShowBookNowDropdown] = useState(false);
  const [dayBookingCancelModal, setDayBookingCancelModal] = useState({ isOpen: false, booking: null, type: null });
  const [declineConfirm, setDeclineConfirm] = useState({ isOpen: false, id: null, type: null });
  const [showAppDownload, setShowAppDownload] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    
    // Auto-refresh every 1 minute
    const interval = setInterval(fetchDashboardData, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [profileRes, ridesRes, dayBookingsRes, requestsRes, completedRes, favoritesRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/riders/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/riders/rides/upcoming`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/day-bookings/rider/bookings`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(err => {
          console.error('Error fetching day bookings:', err);
          return { data: { bookings: [] } };
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/riders/ride-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/riders/rides/history`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(err => {
          console.error('Error fetching ride history:', err);
          return { data: [] };
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/riders/favorites`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(err => {
          console.error('Error fetching favorite drivers:', err);
          return { data: { favorites: [] } };
        })
      ]);

      console.log('🔍 Upcoming rides:', ridesRes.data);
      console.log('🔍 Day bookings:', dayBookingsRes.data);
      console.log('🔍 Ride requests:', requestsRes.data);
      console.log('🔍 Completed rides:', completedRes.data);
      console.log('🔍 Favorite drivers:', favoritesRes.data);

      // Log status of each ride
      if (ridesRes.data && ridesRes.data.length > 0) {
        ridesRes.data.forEach(ride => {
          console.log(`  Ride ${ride.id}: status="${ride.status}"`);
        });
      }

      setProfile(profileRes.data);
      setUpcomingRides(ridesRes.data);
      setDayBookings(dayBookingsRes.data.bookings || []);
      setRideRequests(requestsRes.data);
      setCompletedRides(completedRes.data || []);
      setFavoriteDrivers(favoritesRes.data.favorites || []);

      // Show app download modal for new riders on web (one-time, DB-persisted)
      if (!isNativeApp() && profileRes.data && !profileRes.data.app_download_dismissed) {
        setShowAppDownload(true);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawRequest = async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/rides/ride-requests/${requestId}/withdraw`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Close modal and refresh data
      setWithdrawalModal({ isOpen: false, request: null });
      fetchDashboardData();
    } catch (error) {
      console.error('Error withdrawing ride request:', error);
      alert(error.response?.data?.detail || 'Failed to withdraw ride request');
    }
  };

  // Debug helper to log request data when opening modal
  const openWithdrawalModal = (request) => {
    console.log('Opening withdrawal modal with request data:', request);
    console.log('Available fields:', Object.keys(request));
    setWithdrawalModal({ isOpen: true, request });
  };

  // Filter active rides that need attention
  const getActiveRides = () => {
    const now = new Date();
    return upcomingRides.filter(ride => {
      // Only show rides where rider_status is 'pending' (scheduled) or 'active' (in progress)
      // This makes the active rides independent of driver status
      if (ride.rider_status !== 'pending' && ride.rider_status !== 'active') return false;

      const startTime = new Date(ensureUTC(ride.pickup_datetime));
      const endTime = ride.dropoff_datetime
        ? new Date(ride.dropoff_datetime)
        : new Date(startTime.getTime() + (ride.duration_minutes || 60) * 60000);

      const fifteenMinBefore = new Date(startTime.getTime() - 15 * 60 * 1000);
      const twoHoursAfterEnd = new Date(endTime.getTime() + 2 * 60 * 60 * 1000);

      // Show if ride is within 15 min before start OR hasn't been marked complete and is within 2 hours of end time
      return now >= fifteenMinBefore && now <= twoHoursAfterEnd;
    });
  };

  const handleAcceptCounterOffer = async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/riders/accept-counter-offer/${requestId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.alert('Counter offer accepted!');
      fetchDashboardData(); // Refresh
    } catch (error) {
      console.error('Error accepting counter offer:', error);
      window.alert('Failed to accept counter offer');
    }
  };

  const handleDeclineCounterOffer = async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/riders/decline-counter-offer/${requestId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.alert('Counter offer declined');
      fetchDashboardData(); // Refresh
    } catch (error) {
      console.error('Error declining counter offer:', error);
      window.alert('Failed to decline counter offer');
    }
  };

  // Day Booking Counter-Offer Handlers
  const handleAcceptDayBookingCounterOffer = async (bookingId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/day-bookings/rider/counter-offer/${bookingId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.alert('Counter offer accepted! Waiting for driver to confirm.');
      fetchDashboardData();
    } catch (error) {
      console.error('Error accepting day booking counter offer:', error);
      window.alert(error.response?.data?.detail || 'Failed to accept counter offer');
    }
  };

  const handleDeclineDayBookingCounterOffer = async (bookingId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/day-bookings/rider/counter-offer/${bookingId}/decline`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.alert('Counter offer declined. Booking cancelled.');
      fetchDashboardData();
    } catch (error) {
      console.error('Error declining day booking counter offer:', error);
      window.alert(error.response?.data?.detail || 'Failed to decline counter offer');
    }
  };

  // Day Booking Cancellation Handlers
  const handleWithdrawDayBooking = async (bookingId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/day-bookings/rider/${bookingId}/withdraw`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.alert('Day booking request withdrawn successfully.');
      setDayBookingCancelModal({ isOpen: false, booking: null, type: null });
      fetchDashboardData();
    } catch (error) {
      console.error('Error withdrawing day booking:', error);
      window.alert(error.response?.data?.detail || 'Failed to withdraw day booking');
    }
  };

  const handleCancelScheduledDayBooking = async (bookingId, reason) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/day-bookings/rider/${bookingId}/cancel`,
        { reason: reason || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const refundInfo = response.data;
      if (refundInfo.refund_amount > 0) {
        window.alert(`Booking cancelled. You will receive a refund of $${refundInfo.refund_amount.toFixed(2)} (${refundInfo.refund_percentage}%).`);
      } else {
        window.alert('Booking cancelled. No refund due to late cancellation (less than 6 hours before start time).');
      }
      setDayBookingCancelModal({ isOpen: false, booking: null, type: null });
      fetchDashboardData();
    } catch (error) {
      console.error('Error cancelling day booking:', error);
      window.alert(error.response?.data?.detail || 'Failed to cancel day booking');
    }
  };

  // Calculate refund estimate for day bookings (similar to CancelRideModal logic)
  const calculateDayBookingRefundEstimate = (booking) => {
    if (!booking) return { percentage: 100, amount: 0 };

    const now = new Date();
    const startTime = new Date(booking.start_time.endsWith('Z') ? booking.start_time : booking.start_time + 'Z');

    let bookingTime;
    if (booking.created_at) {
      const createdAtStr = booking.created_at.toString();
      if (createdAtStr.includes('Z') || createdAtStr.includes('+')) {
        bookingTime = new Date(booking.created_at);
      } else {
        bookingTime = new Date(booking.created_at + 'Z');
      }
    } else {
      bookingTime = now;
    }

    const hoursUntil = (startTime - now) / (1000 * 60 * 60);
    const minutesSinceBooking = (now - bookingTime) / (1000 * 60);

    let percentage = 0;
    let scenario = '';

    if (minutesSinceBooking >= 0 && minutesSinceBooking <= 5) {
      percentage = 100;
      const mins = Math.max(0, Math.floor(minutesSinceBooking));
      scenario = `within ${mins} minute${mins === 1 ? '' : 's'} of booking`;
    } else if (hoursUntil >= 24) {
      percentage = 100;
      scenario = `${Math.floor(hoursUntil)} hours before your booking`;
    } else if (hoursUntil >= 12) {
      percentage = 50;
      scenario = `${Math.floor(hoursUntil)} hours before your booking`;
    } else if (hoursUntil >= 6) {
      percentage = 25;
      scenario = `${Math.floor(hoursUntil)} hours before your booking`;
    } else {
      percentage = 0;
      scenario = `${Math.floor(Math.max(0, hoursUntil))} hours before your booking`;
    }

    const baseFare = booking.total_fare || booking.rider_total || 0;
    const amount = (baseFare * percentage) / 100;

    return { percentage, amount, scenario };
  };

  const handleAddTip = (rideId, isDayBooking = false) => {
    // Navigate to dedicated tip page with preset buttons and fare breakdown
    if (isDayBooking) {
      navigate(`/day-bookings/${rideId}/tip`);
    } else {
      navigate(`/rides/${rideId}/tip`);
    }
  };

  // Check if ride is within 24-hour tip window
  const canStillTip = (ride) => {
    // For day bookings, check if completed and within 24 hours of booking end time
    if (ride.is_day_booking) {
      // Day booking must be completed to tip
      if (ride.status !== 'completed') {
        return false;
      }

      // Use dropoff_datetime (which is booking end_time for day bookings in history)
      // For day bookings, we use the booking's end time to calculate the tip window
      const endTimeStr = ride.dropoff_datetime || ride.pickup_datetime;
      if (!endTimeStr) {
        return false;
      }

      // Parse the datetime - it may have timezone offset (e.g., -06:00) or Z suffix
      // If it has neither, treat as UTC
      const hasTimezone = endTimeStr.includes('+') || endTimeStr.includes('-', 10) || endTimeStr.endsWith('Z');
      const timeStr = hasTimezone ? endTimeStr : endTimeStr + 'Z';
      const endTime = new Date(timeStr);
      const now = new Date();
      const hoursSinceEnd = (now - endTime) / (1000 * 60 * 60);

      console.log(`[canStillTip] Day Booking ${ride.booking_id}:`, {
        status: ride.status,
        endTimeStr,
        parsedEndTime: endTime.toISOString(),
        hoursSinceEnd,
        canTip: hoursSinceEnd < 24
      });

      return hoursSinceEnd < 24;
    }

    // For regular autocompleted rides, check if within 24 hours from estimated dropoff time
    if (ride.rider_auto_completed) {
      // Use estimated_dropoff_time or dropoff_datetime (backend alias)
      const dropoffTimeStr = ride.estimated_dropoff_time || ride.dropoff_datetime;
      if (!dropoffTimeStr) {
        console.log(`[canStillTip] Ride ${ride.id}: No dropoff time found`);
        return false;
      }

      // Parse as UTC by appending 'Z' if not already present
      const timeStr = dropoffTimeStr.endsWith('Z') ? dropoffTimeStr : dropoffTimeStr + 'Z';
      const dropoffTime = new Date(timeStr);
      const now = new Date();
      const hoursSinceDropoff = (now - dropoffTime) / (1000 * 60 * 60);

      console.log(`[canStillTip] Ride ${ride.id}:`, {
        rider_auto_completed: ride.rider_auto_completed,
        dropoffTimeStr,
        dropoffTime: dropoffTime.toISOString(),
        now: now.toISOString(),
        hoursSinceDropoff,
        canTip: hoursSinceDropoff < 24
      });

      return hoursSinceDropoff < 24;
    }

    // For manually completed rides, they were already prompted for tip, so don't show tip button
    return false;
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const activeRides = getActiveRides();

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">Rider Dashboard</h1>
            <UserDropdown userType="rider" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Deactivation Banner */}
        {profile?.is_active === false && (
          <div className="bg-red-50 border border-red-300 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <h3 className="text-red-800 font-semibold">Account Deactivated</h3>
                <p className="text-red-700 text-sm">Your account has been deactivated. You can view your ride history and past reports, but cannot browse drivers or request new rides. Contact support@ridehowl.com for assistance.</p>
              </div>
            </div>
          </div>
        )}

        {/* Welcome Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold mb-2">
            Welcome, {profile?.first_name} {profile?.last_name}
          </h2>
          <p className="text-gray-600">{profile?.is_active === false ? 'Your account is currently deactivated.' : 'Ready to book a ride?'}</p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {profile?.is_active !== false && (
            <Link to="/rider/browse-drivers" className="bg-blue-500 text-white rounded-lg shadow p-6 hover:bg-blue-600 transition">
              <div className="text-lg font-semibold">Browse Drivers</div>
              <div className="text-sm mt-2">Find and book a ride</div>
            </Link>
          )}

          {/* Book Now Dropdown - Always show for active riders */}
          {profile?.is_active !== false && (
            <div className="relative">
              <button
                onClick={() => {
                  if (favoriteDrivers.length === 0) {
                    navigate('/rider/browse-drivers');
                  } else {
                    setShowBookNowDropdown(!showBookNowDropdown);
                  }
                }}
                onBlur={() => setTimeout(() => setShowBookNowDropdown(false), 200)}
                className="w-full h-full bg-green-500 text-white rounded-lg shadow p-6 hover:bg-green-600 transition flex flex-col items-start justify-center"
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold">Book Now</span>
                  {favoriteDrivers.length > 0 && (
                    <svg className={`w-5 h-5 transition-transform ${showBookNowDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </div>
                <div className="text-sm mt-2">
                  {favoriteDrivers.length > 0 ? 'Quick book with favorites' : 'Browse drivers and book a ride'}
                </div>
              </button>

              {/* Dropdown Menu */}
              {showBookNowDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
                  <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                    <span className="text-sm font-medium text-gray-600">Select a favorite driver</span>
                  </div>
                  {favoriteDrivers.map(driver => (
                    <button
                      key={driver.id}
                      onClick={() => {
                        setShowBookNowDropdown(false);
                        navigate('/request-ride', { state: { driver } });
                      }}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-green-50 transition border-b border-gray-100 last:border-b-0"
                    >
                      {driver.profile_picture_url ? (
                        <img
                          src={driver.profile_picture_url}
                          alt={`${driver.first_name} ${driver.last_name}`}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-sm font-bold text-green-700">
                          {driver.first_name?.[0]}{driver.last_name?.[0]}
                        </div>
                      )}
                      <div className="flex-1 text-left">
                        <div className="font-medium text-gray-900">
                          {driver.first_name} {driver.last_name}
                        </div>
                        {driver.vehicle_make && (
                          <div className="text-xs text-gray-500">
                            {driver.vehicle_year} {driver.vehicle_make} {driver.vehicle_model}
                          </div>
                        )}
                        {driver.max_passenger_capacity && (
                          <div className="text-xs text-gray-500">
                            {driver.max_passenger_capacity} passenger{driver.max_passenger_capacity > 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                      <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-3xl font-bold text-green-500 mb-2">{upcomingRides.length}</div>
            <div className="text-gray-600">Upcoming Rides</div>
          </div>

          <Link to="/rider/history" className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
            <div className="text-sm text-gray-600">View</div>
            <div className="text-lg font-semibold">Ride History</div>
          </Link>
        </div>

        {/* Active Rides Section */}
        {activeRides.length > 0 && (
          <div className="mb-8">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-4 rounded-t-lg">
              <div className="flex items-center gap-3">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <div>
                  <h2 className="text-2xl font-bold">Active Rides</h2>
                  <p className="text-sm opacity-90">
                    {activeRides.length} {activeRides.length === 1 ? 'ride' : 'rides'} in progress
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-b-lg shadow-lg p-6">
              {activeRides.map(ride => (
                <ActiveRideCard
                  key={ride.id}
                  ride={ride}
                  onCancel={() => fetchDashboardData()}
                />
              ))}
            </div>
          </div>
        )}

        {/* Counter Offers Section */}
        {rideRequests.filter(r => r.status === 'counter_offered').length > 0 && (
          <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg shadow p-6 mb-6">
            <h3 className="text-xl font-bold mb-4 text-yellow-800">⚠️ Counter Offers Pending</h3>
            <div className="space-y-4">
              {rideRequests.filter(r => r.status === 'counter_offered').map(request => (
                <div key={request.id} className="bg-white border border-yellow-300 rounded-lg p-4">
                  {request.driver_name && (
                    <div className="text-sm font-medium text-blue-600 mb-2">
                      🚗 Driver: {request.driver_name}
                    </div>
                  )}
                  <div className="font-semibold text-lg mb-2">
                    {request.pickup_address || request.pickup_location} → {request.dropoff_address || request.dropoff_location}
                  </div>

                  <div className="space-y-3 mb-3 text-sm">
                    {/* Original Price Breakdown */}
                    <div className="bg-gray-50 rounded p-2">
                      <div className="text-gray-500 text-xs mb-1">Original Quote:</div>
                      <div className="line-through text-gray-400">
                        <span>Base fare: ${parseFloat(request.quoted_price).toFixed(2)}</span>
                        <span className="mx-1">+</span>
                        <span>Processing fee: ${calculateProcessingFee(request.quoted_price).toFixed(2)}</span>
                        <span className="mx-1">=</span>
                        <span className="font-semibold">${calculateTotalWithFee(request.quoted_price).toFixed(2)} total</span>
                      </div>
                    </div>

                    {/* Counter Offer Breakdown */}
                    {request.counter_offer_price && (
                      <div className="bg-green-50 border border-green-200 rounded p-2">
                        <div className="text-green-700 text-xs mb-1">Counter Offer:</div>
                        <div className="text-green-700">
                          <span>Base fare: ${parseFloat(request.counter_offer_price).toFixed(2)}</span>
                          <span className="mx-1">+</span>
                          <span>Processing fee: ${calculateProcessingFee(request.counter_offer_price).toFixed(2)}</span>
                          <span className="mx-1">=</span>
                          <span className="font-bold">${calculateTotalWithFee(request.counter_offer_price).toFixed(2)} total</span>
                        </div>
                      </div>
                    )}

                    <div>
                      <span className="text-gray-600">Pickup Time:</span>
                      <span className="ml-2">{formatDateTime(request.requested_pickup_time, 'full')}</span>
                    </div>
                    {request.counter_offer_reason && (
                      <div>
                        <span className="text-gray-600">Driver's Note:</span>
                        <span className="ml-2 italic text-gray-700">"{request.counter_offer_reason}"</span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={() => handleAcceptCounterOffer(request.id)}
                      className="flex-1 bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 font-semibold"
                    >
                      Accept Counter Offer
                    </button>
                    <button
                      onClick={() => setDeclineConfirm({ isOpen: true, id: request.id, type: 'ride' })}
                      className="flex-1 bg-red-500 text-white py-2 px-4 rounded-lg hover:bg-red-600 font-semibold"
                    >
                      Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Day Booking Counter Offers Section */}
        {dayBookings.filter(b => b.status === 'counter_offered').length > 0 && (
          <div className="bg-orange-50 border-2 border-orange-400 rounded-lg shadow p-6 mb-6">
            <h3 className="text-xl font-bold mb-4 text-orange-800">📅 Day Booking Counter Offers</h3>
            <div className="space-y-4">
              {dayBookings.filter(b => b.status === 'counter_offered').map(booking => (
                <div key={booking.id} className="bg-white border border-orange-300 rounded-lg p-4">
                  <div className="text-sm font-medium text-blue-600 mb-2">
                    🚗 Driver: {booking.driver_name}
                  </div>
                  <div className="font-semibold text-lg mb-2">
                    {booking.rate_type === 'half_day' ? '🌅 Half Day' : '☀️ Full Day'} Booking
                  </div>
                  <div className="text-sm text-gray-600 mb-3">
                    📅 {booking.booking_date} • {new Date(ensureUTC(booking.start_time)).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - {new Date(ensureUTC(booking.end_time)).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </div>

                  <div className="space-y-3 mb-3 text-sm">
                    {/* Original Price Breakdown */}
                    <div className="bg-gray-50 rounded p-2">
                      <div className="text-gray-500 text-xs mb-1">Original Quote:</div>
                      <div className="line-through text-gray-400">
                        <span>Flat rate: ${parseFloat(booking.flat_rate).toFixed(2)}</span>
                        <span className="mx-1">+</span>
                        <span>Processing fee: ${calculateProcessingFee(booking.flat_rate).toFixed(2)}</span>
                        <span className="mx-1">=</span>
                        <span className="font-semibold">${calculateTotalWithFee(booking.flat_rate).toFixed(2)} total</span>
                      </div>
                    </div>

                    {/* Counter Offer Breakdown */}
                    <div className="bg-green-50 border border-green-200 rounded p-2">
                      <div className="text-green-700 text-xs mb-1">Counter Offer:</div>
                      <div className="text-green-700">
                        <span>Flat rate: ${parseFloat(booking.counter_offer_flat_rate).toFixed(2)}</span>
                        <span className="mx-1">+</span>
                        <span>Processing fee: ${calculateProcessingFee(booking.counter_offer_flat_rate).toFixed(2)}</span>
                        <span className="mx-1">=</span>
                        <span className="font-bold">${calculateTotalWithFee(booking.counter_offer_flat_rate).toFixed(2)} total</span>
                      </div>
                    </div>

                    <div>
                      <span className="text-gray-600">Total Mileage:</span>
                      <span className="ml-2">{parseFloat(booking.total_mileage).toFixed(1)} miles</span>
                    </div>
                    {booking.counter_offer_reason && (
                      <div>
                        <span className="text-gray-600">Reason:</span>
                        <span className="ml-2 italic">{booking.counter_offer_reason}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={() => handleAcceptDayBookingCounterOffer(booking.id)}
                      className="flex-1 bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 font-semibold"
                    >
                      Accept Counter Offer
                    </button>
                    <button
                      onClick={() => setDeclineConfirm({ isOpen: true, id: booking.id, type: 'dayBooking' })}
                      className="flex-1 bg-red-500 text-white py-2 px-4 rounded-lg hover:bg-red-600 font-semibold"
                    >
                      Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending Requests */}
        {rideRequests.filter(r => r.status === 'pending').length > 0 && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-xl font-bold mb-4">Pending Ride Requests</h3>
            <div className="space-y-4">
              {rideRequests.filter(r => r.status === 'pending').map(request => (
                <div key={request.id} className="border rounded-lg p-4 relative">
                  {/* Withdraw button (X) */}
                  <button
                    onClick={() => openWithdrawalModal(request)}
                    className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition"
                    title="Withdraw ride request"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>

                  {request.driver_name && (
                    <div className="text-sm font-medium text-blue-600 mb-2">
                      🚗 Driver: {request.driver_name}
                    </div>
                  )}
                  <div className="font-semibold pr-8">
                    {request.pickup_address || request.pickup_location} → {request.dropoff_address || request.dropoff_location}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {formatDateTime(request.requested_pickup_time, 'full')}
                  </div>
                  <div className="text-sm text-gray-600">
                    ${request.quoted_price} • {request.distance_miles} miles
                  </div>
                  <span className="inline-block mt-2 px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                    Waiting for driver response
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upcoming Rides */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-bold mb-4">Upcoming Rides</h3>
          {upcomingRides.length === 0 ? (
            <p className="text-gray-500">No upcoming rides scheduled</p>
          ) : (
            <div className="space-y-4">
              {upcomingRides.map(ride => (
                <UpcomingRideCard
                  key={ride.id}
                  ride={ride}
                  onCancel={() => fetchDashboardData(true)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Day Bookings */}
        {dayBookings.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6 mt-6">
            <h3 className="text-xl font-bold mb-4">📅 Day Bookings</h3>
            <div className="space-y-4">
              {dayBookings.map(booking => (
                <div key={booking.id} className="bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs font-semibold px-2 py-1 rounded ${
                          booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          booking.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                          booking.status === 'in_progress' ? 'bg-green-100 text-green-800' :
                          booking.status === 'completed' ? 'bg-gray-100 text-gray-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {booking.status.replace('_', ' ').toUpperCase()}
                        </span>
                        <span className="text-sm font-medium text-gray-600">
                          {new Date(ensureUTC(booking.start_time)).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',

                          })}
                        </span>
                      </div>
                      <h4 className="text-lg font-bold text-orange-800 mb-1">
                        {booking.rate_type === 'half_day' ? '🌅 Half-Day Booking' : '☀️ Full-Day Booking'}
                      </h4>
                      <div className="text-sm text-gray-700 space-y-1">
                        <div>
                          <span className="font-medium">Time:</span>{' '}
                          {new Date(booking.start_time.endsWith('Z') ? booking.start_time : booking.start_time + 'Z').toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                            
                          })} CT -{' '}
                          {new Date(booking.end_time.endsWith('Z') ? booking.end_time : booking.end_time + 'Z').toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                            
                          })} CT
                        </div>
                        <div>
                          <span className="font-medium">Driver:</span> {booking.driver_name || 'N/A'}
                        </div>
                        <div>
                          <span className="font-medium">Total Distance:</span> {booking.total_mileage} miles
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-orange-600">
                        ${booking.rider_total ? booking.rider_total.toFixed(2) : booking.total_fare}
                      </div>
                      <div className="text-xs text-gray-600">
                        Tier {booking.mileage_tier_used}
                      </div>
                      {booking.fare_processing_fee > 0 && (
                        <div className="text-xs text-gray-500 mt-1">
                          (incl. ${booking.fare_processing_fee.toFixed(2)} processing fee)
                        </div>
                      )}
                    </div>
                  </div>
                  {booking.status === 'pending' && (
                    <div className="flex justify-between items-center border-t pt-2 mt-2">
                      <div className="text-xs text-gray-600 italic">
                        Waiting for driver to accept
                      </div>
                      <button
                        onClick={() => setDayBookingCancelModal({ isOpen: true, booking, type: 'withdraw' })}
                        className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded border border-red-300 transition"
                      >
                        Withdraw Request
                      </button>
                    </div>
                  )}
                  {booking.status === 'scheduled' && (
                    <div className="flex justify-between items-center border-t pt-2 mt-2">
                      <div className="text-xs text-green-700 font-medium">
                        Confirmed - Driver will be available during your entire booking window
                      </div>
                      <button
                        onClick={() => setDayBookingCancelModal({ isOpen: true, booking, type: 'cancel' })}
                        className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded border border-red-300 transition"
                      >
                        Cancel Booking
                      </button>
                    </div>
                  )}
                  {booking.status === 'in_progress' && (
                    <div className="text-xs text-green-700 font-medium border-t pt-2">
                      In Progress - Ride is currently active
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Completed Rides */}
        {completedRides.filter(ride => ride.status === 'completed').length > 0 && (
          <div className="bg-white rounded-lg shadow p-6 mt-6">
            <h3 className="text-xl font-bold mb-4">Recent Completed Rides</h3>
            <div className="space-y-4">
              {completedRides.filter(ride => ride.status === 'completed').slice(0, 5).map(ride => (
                <div key={ride.id} className="border rounded-lg p-4">
                  {/* Day Booking Summary */}
                  {ride.is_day_booking ? (
                    <>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="inline-block px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded font-medium">
                              {ride.rate_type === 'half_day' ? 'Half-Day' : 'Full-Day'} ({ride.leg_count} {ride.leg_count === 1 ? 'trip' : 'trips'})
                            </span>
                          </div>
                          <div className="font-semibold text-orange-800">
                            {ride.rate_type === 'half_day' ? '🌅 Half-Day Booking' : '☀️ Full-Day Booking'}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            {ride.pickup_datetime ? new Date(ensureUTC(ride.pickup_datetime)).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              
                            }) : 'N/A'}
                          </div>
                          <div className="text-sm text-gray-600">
                            Driver: {ride.driver_name || 'N/A'} • {(ride.distance_miles || 0).toFixed(1)} miles total
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-orange-600">
                            ${(() => {
                              const fare = parseFloat(ride.total_fare || ride.flat_rate || 0);
                              const tip = parseFloat(ride.tip_amount || 0);
                              // Stripe pass-through formula: (fare + $0.30) / 0.971 for fares, tip / 0.971 for tips
                              const fareGross = (fare + 0.30) / 0.971;
                              const tipGross = tip > 0 ? tip / 0.971 : 0;
                              return (fareGross + tipGross).toFixed(2);
                            })()}
                          </div>
                          <div className="text-xs text-gray-500">Total Amount Paid</div>
                          <span className="inline-block mt-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                            Completed
                          </span>
                        </div>
                      </div>

                      {/* Add Tip Button for Day Bookings */}
                      {canStillTip(ride) && (!ride.tip_amount || ride.tip_amount === 0) && (
                        <button
                          onClick={() => handleAddTip(ride.booking_id, true)}
                          className="mt-3 w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 font-semibold text-sm"
                        >
                          💰 Add Tip (24-hour window from ride completion)
                        </button>
                      )}

                      {!canStillTip(ride) && (!ride.tip_amount || ride.tip_amount === 0) && (
                        <div className="mt-3 text-xs text-gray-500 text-center italic">
                          Tip window expired (24 hours from completion)
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Regular Ride */}
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-semibold">
                            {ride.pickup_address || ride.pickup_location} → {ride.dropoff_address || ride.dropoff_location}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            {ride.pickup_datetime ? new Date(ensureUTC(ride.pickup_datetime)).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              
                            }) : 'N/A'}
                          </div>
                          <div className="text-sm text-gray-600">
                            Driver: {ride.driver_name || 'N/A'}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-green-600">
                            ${(() => {
                              const fare = parseFloat(ride.total_fare || ride.final_price || 0);
                              const tip = parseFloat(ride.tip_amount || 0);
                              // Stripe pass-through formula: (fare + $0.30) / 0.971 for fares, tip / 0.971 for tips
                              const fareGross = (fare + 0.30) / 0.971;
                              const tipGross = tip > 0 ? tip / 0.971 : 0;
                              return (fareGross + tipGross).toFixed(2);
                            })()}
                          </div>
                          <div className="text-xs text-gray-500">Total Amount Paid</div>
                          <span className="inline-block mt-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                            Completed
                          </span>
                        </div>
                      </div>

                      {/* Add Tip Button - Only show if autocompleted within 24-hour window and no tip yet */}
                      {canStillTip(ride) && (!ride.tip_amount || ride.tip_amount === 0) && (
                        <button
                          onClick={() => handleAddTip(ride.id)}
                          className="mt-3 w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 font-semibold text-sm"
                        >
                          💰 Add Tip (24-hour window from ride completion)
                        </button>
                      )}

                      {/* Tip Window Expired Message - Only for autocompleted rides */}
                      {ride.rider_auto_completed && !canStillTip(ride) && (!ride.tip_amount || ride.tip_amount === 0) && (
                        <div className="mt-3 text-xs text-gray-500 text-center italic">
                          Tip window expired (24 hours from completion)
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
            <Link
              to="/rider/history"
              className="block mt-4 text-blue-500 hover:underline text-center"
            >
              View Full History ({completedRides.length} total)
            </Link>
          </div>
        )}
      </main>

      {/* Withdrawal Confirmation Modal */}
      {withdrawalModal.isOpen && withdrawalModal.request && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Withdraw Ride Request?</h2>
              </div>
            </div>

            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Route:</strong> {withdrawalModal.request.pickup_address} → {withdrawalModal.request.dropoff_address}
              </p>
              <p className="text-sm text-gray-600 mb-2">
                <strong>Pickup:</strong> {withdrawalModal.request.requested_pickup_time ? new Date(ensureUTC(withdrawalModal.request.requested_pickup_time)).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  
                }) : 'N/A'}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Price:</strong> ${withdrawalModal.request.quoted_price}
              </p>
            </div>

            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>No Charge:</strong> Your payment method will not be charged since the driver has not accepted yet.
              </p>
            </div>

            <p className="text-gray-700 mb-6">
              Are you sure you want to withdraw your ride request? This cannot be undone.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setWithdrawalModal({ isOpen: false, request: null })}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => handleWithdrawRequest(withdrawalModal.request.id)}
                className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition"
              >
                Yes, Withdraw
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Day Booking Cancellation Modal */}
      {dayBookingCancelModal.isOpen && dayBookingCancelModal.booking && (
        <DayBookingCancelModal
          booking={dayBookingCancelModal.booking}
          type={dayBookingCancelModal.type}
          onClose={() => setDayBookingCancelModal({ isOpen: false, booking: null, type: null })}
          onWithdraw={handleWithdrawDayBooking}
          onCancel={handleCancelScheduledDayBooking}
          calculateRefundEstimate={calculateDayBookingRefundEstimate}
        />
      )}

      <ConfirmModal
        isOpen={declineConfirm.isOpen}
        onClose={() => setDeclineConfirm({ isOpen: false, id: null, type: null })}
        onConfirm={() => {
          if (declineConfirm.type === 'dayBooking') {
            handleDeclineDayBookingCounterOffer(declineConfirm.id);
          } else {
            handleDeclineCounterOffer(declineConfirm.id);
          }
        }}
        title="Decline Counter Offer"
        message={declineConfirm.type === 'dayBooking'
          ? 'Are you sure you want to decline this counter offer? The booking will be cancelled.'
          : 'Are you sure you want to decline this counter offer?'}
        confirmText="Decline"
        confirmColor="red"
      />

      {/* App Download Modal — shown once for new riders on web */}
      <AppDownloadModal
        isOpen={showAppDownload}
        onClose={() => {
          setShowAppDownload(false);
          // Fire-and-forget API call to persist dismissal
          const token = localStorage.getItem('token');
          axios.post(
            `${process.env.REACT_APP_API_URL}/api/riders/app-download-dismissed`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          ).catch(err => console.error('Failed to save app download dismissal:', err));
        }}
        title="Never Miss a Ride Update"
        subtitle="Download the RideHowl app for real-time booking confirmations, driver updates, and ride status notifications right on your phone."
        iosOnly={true}
        dismissLabel="Maybe Later"
      />
    </div>
  );
};

export default RiderDashboard;
